import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, DataTable, StatusBadge, Sel, Field } from "@/components/kit";
import { api, useApi, fmtErr, dt, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function UserForm({ init, meta, onClose, onSaved }) {
  const { branches } = useAuth();
  const [f, setF] = useState(init);
  const toggle = (k, v) => setF({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] });
  const setRole = (role) => setF({ ...f, role, permissions: init.id ? f.permissions : meta.role_defaults[role], all_branches: init.id ? f.all_branches : ["super_admin", "head_office_accounting", "it", "finance"].includes(role) });
  const save = async () => {
    try {
      f.id ? await api.put(`/users/${f.id}`, f) : await api.post("/users", f);
      toast.success("User saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-card" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name"><Input data-testid="user-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Email"><Input data-testid="user-email" disabled={!!f.id} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Title"><Input data-testid="user-title" value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Role"><Sel value={f.role} onChange={setRole} testid="user-role" className="w-full" options={Object.entries(meta.roles).map(([v, l]) => ({ value: v, label: l }))} /></Field>
          <Field label={f.id ? "New password (optional)" : "Password"}><Input data-testid="user-password" type="password" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
          <div className="flex items-end gap-4 pb-2">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="user-active" /> Active</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.all_branches} onCheckedChange={(v) => setF({ ...f, all_branches: v })} data-testid="user-all-branches" /> All branches</label>
          </div>
        </div>
        {!f.all_branches && (
          <Field label="Assigned branches">
            <div className="flex flex-wrap gap-3">{branches.map((b) => (
              <label key={b.id} className="flex items-center gap-2 text-sm"><Checkbox checked={f.branch_ids.includes(b.id)} onCheckedChange={() => toggle("branch_ids", b.id)} data-testid={`user-branch-${b.code}`} />{b.code}</label>
            ))}</div>
          </Field>
        )}
        <Field label="Permissions (configurable per user)">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{meta.permissions.map((p) => (
            <label key={p} className="flex items-center gap-2 text-sm"><Checkbox checked={f.permissions.includes(p)} onCheckedChange={() => toggle("permissions", p)} data-testid={`user-perm-${p}`} />{label(p)}</label>
          ))}</div>
        </Field>
        <Button data-testid="user-save" onClick={save}>Save user</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { branchName } = useAuth();
  const { data, loading, reload } = useApi("/users", {});
  const meta = useApi("/meta", {});
  const [edit, setEdit] = useState(null);
  return (
    <div className="fade-up">
      <PageHeader title="Users & Roles" subtitle="Role-based access with per-user permission overrides" testid="users-header">
        {meta.data && <Button data-testid="user-add-btn" onClick={() => setEdit({ name: "", email: "", role: "roving_attendant", permissions: meta.data.role_defaults.roving_attendant, branch_ids: [], all_branches: false, active: true })}><Plus className="h-4 w-4" /> User</Button>}
      </PageHeader>
      <DataTable testid="users-table" loading={loading} rows={data} onRowClick={(r) => meta.data && setEdit({ ...r, password: "" })} columns={[
        { key: "name", label: "Name", render: (r) => <span><b>{r.name}</b> <span className="text-xs text-muted-foreground">{r.title}</span></span> },
        { key: "email", label: "Email" },
        { key: "role_label", label: "Role", render: (r) => <StatusBadge status={r.role_label} tone={r.role === "super_admin" ? "amber" : "blue"} /> },
        { key: "branches", label: "Branches", render: (r) => (r.all_branches ? "All" : r.branch_ids.map(branchName).join(", ") || "—") },
        { key: "permissions", label: "Permissions", render: (r) => <span className="text-xs text-muted-foreground">{r.permissions.length} / 15</span> },
        { key: "active", label: "Status", render: (r) => <StatusBadge status={r.active ? "active" : "disabled"} /> },
        { key: "last_login", label: "Last login", render: (r) => dt(r.last_login) },
      ]} />
      {edit && meta.data && <UserForm init={edit} meta={meta.data} onClose={() => setEdit(null)} onSaved={reload} />}
    </div>
  );
}
